import { Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useRole } from '@/hooks/useRole';
import { useProfile } from '@/hooks/useProfile';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Clock, LogOut, RefreshCw } from 'lucide-react';
import logo from '@/assets/logo.svg';

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading, signOut } = useAuth();
  const location = useLocation();
  const { isAdmin, isLoading: roleLoading } = useRole();
  const { profile, isLoading: profileLoading } = useProfile();
  const queryClient = useQueryClient();

  // Realtime: refresh profile the moment an admin updates approval_status
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`profile-${user.id}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'profiles', filter: `user_id=eq.${user.id}` },
        () => {
          queryClient.invalidateQueries({ queryKey: ['profile', user.id] });
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, queryClient]);

  // Soft-poll every 15s while pending, as a fallback in case realtime is unavailable
  const approvalStatus = (profile as any)?.approval_status as string | undefined;
  useEffect(() => {
    if (!user || approvalStatus === 'approved') return;
    const t = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ['profile', user.id] });
    }, 15000);
    return () => clearInterval(t);
  }, [user, approvalStatus, queryClient]);

  if (loading || profileLoading || roleLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  }

  // Admins bypass approval check
  if (!isAdmin && approvalStatus && approvalStatus !== 'approved') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <div className="w-full max-w-sm text-center animate-fade-in">
          <img src={logo} alt="Amerikooler" className="mx-auto mb-6 h-14 w-auto" />
          {approvalStatus === 'rejected' ? (
            <>
              <div className="flex h-16 w-16 mx-auto mb-4 items-center justify-center rounded-2xl bg-destructive/10">
                <span className="text-2xl">❌</span>
              </div>
              <h2 className="text-xl font-bold text-foreground mb-2">Account Not Approved</h2>
              <p className="text-sm text-muted-foreground mb-6">
                Your dealer account application has been reviewed and was not approved. Please contact Amerikooler for more information.
              </p>
            </>
          ) : (
            <>
              <div className="flex h-16 w-16 mx-auto mb-4 items-center justify-center rounded-2xl bg-warning/10">
                <Clock className="h-8 w-8 text-warning" />
              </div>
              <h2 className="text-xl font-bold text-foreground mb-2">Pending Approval</h2>
              <p className="text-sm text-muted-foreground mb-6">
                Your dealer account is being reviewed by the Amerikooler team. You'll receive access once approved.
              </p>
            </>
          )}
          <div className="flex flex-col gap-2 items-center">
            {approvalStatus !== 'rejected' && (
              <Button
                onClick={() => queryClient.invalidateQueries({ queryKey: ['profile', user.id] })}
                className="gap-2 rounded-xl w-full"
              >
                <RefreshCw className="h-4 w-4" />
                Check status
              </Button>
            )}
            <Button variant="outline" onClick={() => signOut()} className="gap-2 rounded-xl w-full">
              <LogOut className="h-4 w-4" />
              Sign Out
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
